import { injectable, inject } from '@patsnap/tahiti-core/shared/inversify';
import type { MonitorAgentService, MonitorAgentClickParams } from '../monitor-agent-service';
import type { AgentApi } from '@/domains/home/apis/agent-api';
import { AgentApi as AgentApiSymbol } from '@/domains/home/apis/agent-api';
import { isInIframe } from '@telecom/shared/utils/in-iframe';
import { sendRouterChangeMessage } from '@/utils/send-parent-message';

/**
 * Monitor Agent 服务实现
 * 负责处理 monitor agent 的点击事件
 */
@injectable()
export class MonitorAgentServiceImpl implements MonitorAgentService {
    constructor(
        @inject(AgentApiSymbol)
        private _agentApi: AgentApi,
    ) {}

    /**
     * 处理 Monitor Agent 点击事件
     * 1. 调用 Agent API 创建 chat job 获取 job_id
     * 2. 路由跳转到 /monitor?job_id=xxx
     */
    async handleClick(params: MonitorAgentClickParams): Promise<void> {
        const { agentId, teamId } = params;

        // 构造 Agent API 请求参数
        const request = {
            input: '',
            agent_ids: agentId ? [agentId] : [],
            team_ids: teamId ? [teamId] : [],
        };

        try {
            // 调用 Agent API 获取 job_id
            const { job_id } = await this._agentApi.chatJob(request);

            if (!job_id) {
                throw new Error('Failed to get job_id from Agent API');
            }

            // 路由跳转到 monitor 页面，带上 job_id
            this.navigateToMonitor(job_id);
        } catch (error) {
            console.error('Failed to handle monitor agent click:', error);
            throw error;
        }
    }

    /**
     * 跳转到 monitor 页面
     */
    private navigateToMonitor(jobId: string): void {
        const path = '/monitor/#/chat';

        if (isInIframe()) {
            // 在 iframe 中，通过 postMessage 通知父窗口跳转
            sendRouterChangeMessage({ path, useNativeNavigation: true });
        } else {
            // 不在 iframe 中，暂不支持
            console.warn('暂不支持在非 iframe 环境中跳转:', path);
        }
    }
}
